import { useState } from 'react'
import { Link } from 'react-router-dom'
import { MoreHorizontal, Copy, ExternalLink, BarChart2, Trash2, Pencil, Hash, FileText, Radio, MousePointer, Activity, Calendar } from 'lucide-react'
import { toast } from 'sonner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { linkTrackerApi } from '../api'
import { EditURLDialog } from './EditURLDialog'
import type { URLAcortada } from '../types'

interface URLTableProps {
  urls: URLAcortada[]
  onRefresh: () => void
}

export function URLTable({ urls, onRefresh }: URLTableProps) {
  const [loadingAction, setLoadingAction] = useState<string | null>(null)
  const [editingURL, setEditingURL] = useState<URLAcortada | null>(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)

  const handleCopy = (codigo: string) => {
    const url = linkTrackerApi.getURLConTracking(codigo)

    // Ejecutar después de que el menú se cierre
    setTimeout(() => {
      const textarea = document.createElement('textarea')
      textarea.value = url
      textarea.style.cssText = 'position:fixed;left:0;top:0;opacity:0;'
      document.body.appendChild(textarea)
      textarea.focus()
      textarea.select()

      let copied = false
      try {
        copied = document.execCommand('copy')
      } catch {
        copied = false
      }

      document.body.removeChild(textarea)

      if (copied) {
        toast.success('URL copiada al portapapeles')
      } else {
        // Fallback: mostrar en toast para copiar manualmente
        toast.info(`URL: ${url}`, { duration: 10000 })
      }
    }, 150)
  }

  const handleToggle = async (codigo: string) => {
    setLoadingAction(codigo)
    try {
      const result = await linkTrackerApi.toggleURLStatus(codigo)
      toast.success(result.message)
      onRefresh()
    } catch (error) {
      toast.error('Error al cambiar estado')
    } finally {
      setLoadingAction(null)
    }
  }

  const handleDelete = async (codigo: string) => {
    if (!confirm('¿Estás seguro de eliminar esta URL? Esta acción no se puede deshacer.')) return

    setLoadingAction(codigo)
    try {
      await linkTrackerApi.eliminarURL(codigo)
      toast.success('URL eliminada')
      onRefresh()
    } catch (error) {
      toast.error('Error al eliminar')
    } finally {
      setLoadingAction(null)
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-EC', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  if (urls.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <p>No se encontraron URLs</p>
      </div>
    )
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)] [&_tr]:hover:bg-gray-100 dark:[&_tr]:hover:bg-[hsl(220,15%,16%)]">
          <TableRow>
            <TableHead><div className="flex items-center gap-2"><Hash className="h-4 w-4" />Código</div></TableHead>
            <TableHead><div className="flex items-center gap-2"><FileText className="h-4 w-4" />Título</div></TableHead>
            <TableHead><div className="flex items-center gap-2"><Radio className="h-4 w-4" />Canal</div></TableHead>
            <TableHead className="text-center"><div className="flex items-center justify-center gap-2"><MousePointer className="h-4 w-4" />Clics</div></TableHead>
            <TableHead><div className="flex items-center gap-2"><Calendar className="h-4 w-4" />Creada</div></TableHead>
            <TableHead><div className="flex items-center gap-2"><Activity className="h-4 w-4" />Estado</div></TableHead>
            <TableHead className="text-right"></TableHead>
          </TableRow>
        </TableHeader>
      <TableBody>
        {urls.map((url) => (
          <TableRow key={url.id}>
            <TableCell className="font-mono font-medium">
              {url.codigo}
            </TableCell>
            <TableCell className="max-w-[200px] truncate">
              {url.titulo || <span className="text-muted-foreground italic">Sin título</span>}
            </TableCell>
            <TableCell>
              <Badge className="badge-gradient hover:opacity-90 capitalize">
                {url.canal}
              </Badge>
            </TableCell>
            <TableCell className="text-center">
              <span className="font-semibold">{url.total_clics}</span>
              {url.clics_unicos > 0 && (
                <span className="text-xs text-muted-foreground ml-1">
                  ({url.clics_unicos} únicos)
                </span>
              )}
            </TableCell>
            <TableCell className="text-muted-foreground">
              {formatDate(url.created_at)}
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-2">
                <Switch
                  checked={url.activa}
                  onCheckedChange={() => handleToggle(url.codigo)}
                  disabled={loadingAction === url.codigo}
                  className={url.activa ? 'data-[state=checked]:bg-teal-500' : 'data-[state=unchecked]:bg-gray-300'}
                />
                <Badge className={url.activa ? 'badge-gradient hover:opacity-90' : 'bg-gray-100 text-gray-500 hover:bg-gray-100'}>
                  {url.activa ? 'Activa' : 'Inactiva'}
                </Badge>
              </div>
            </TableCell>
            <TableCell className="text-right">
              <div className="flex items-center justify-end gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  asChild
                >
                  <Link to={`/link-tracker/analytics/${url.codigo}`}>
                    <BarChart2 className="h-4 w-4" />
                  </Link>
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={loadingAction === url.codigo}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={() => {
                      setEditingURL(url)
                      setEditDialogOpen(true)
                    }}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Editar
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => handleCopy(url.codigo)}>
                      <Copy className="h-4 w-4 mr-2" />
                      Copiar URL
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <a href={url.url_destino} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Abrir destino
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-red-600 focus:text-red-600"
                      onClick={() => handleDelete(url.codigo)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Eliminar
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
      <EditURLDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        url={editingURL}
        onSuccess={onRefresh}
      />
    </div>
  )
}
